/**
 * AI Recruit - Complexity Level Management Page
 * Admin page for the complexity levels proctored interview questions can be
 * pitched at (e.g. Foundational / Intermediate / Advanced). The description
 * is what the AI uses as that level's definition when generating questions.
 */

import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  fetchComplexityLevels, createComplexityLevel, updateComplexityLevel, deleteComplexityLevel,
  type ComplexityLevel,
} from '@/services/complexityLevelApi'
import { usePermission } from '@/hooks/usePermission'

interface LevelFormState {
  name: string
  description: string
}

const EMPTY_FORM: LevelFormState = { name: '', description: '' }

const ComplexityLevelManagementPage: React.FC = () => {
  // Each action has its own capability (complexityLevels.create/edit/delete),
  // matching the backend's gated() checks in complexity_levels.py.
  const { can } = usePermission()
  const canCreate = can('complexityLevels.create')
  const canEdit = can('complexityLevels.edit')
  const canDelete = can('complexityLevels.delete')
  const queryClient = useQueryClient()

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<ComplexityLevel | null>(null)
  const [form, setForm] = useState<LevelFormState>(EMPTY_FORM)

  const { data: levels, isLoading } = useQuery({
    queryKey: ['complexity-levels'],
    queryFn: fetchComplexityLevels,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['complexity-levels'] })

  const closeForm = () => {
    setShowForm(false)
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  const createMutation = useMutation({
    mutationFn: createComplexityLevel,
    onSuccess: () => { toast.success('Complexity level created'); invalidate(); closeForm() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to create complexity level'),
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: LevelFormState }) => updateComplexityLevel(id, data),
    onSuccess: () => { toast.success('Complexity level updated'); invalidate(); closeForm() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to update complexity level'),
  })
  const deleteMutation = useMutation({
    mutationFn: deleteComplexityLevel,
    onSuccess: () => { toast.success('Complexity level deleted'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to delete complexity level'),
  })

  const startEdit = (level: ComplexityLevel) => {
    setEditing(level)
    setShowForm(true)
    setForm({ name: level.name, description: level.description || '' })
  }

  const submit = () => {
    if (!form.name.trim()) { toast.error('Name is required'); return }
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: form })
    } else {
      createMutation.mutate({ name: form.name, description: form.description || undefined })
    }
  }

  const confirmDelete = (level: ComplexityLevel) => {
    if (!window.confirm(`Delete "${level.name}"? Questions already generated at this level keep it; it just won't be offered for new ones.`)) return
    deleteMutation.mutate(level.id)
  }

  if (isLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
  }

  const saving = createMutation.isPending || updateMutation.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Complexity Levels</h2>
          <p className="text-sm text-gray-500">
            The complexity levels you can pick for each question type when generating a proctored interview.
            The description tells the AI what each level means.
          </p>
        </div>
        {canCreate && !showForm && (
          <button
            onClick={() => { setShowForm(true); setEditing(null); setForm(EMPTY_FORM) }}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4" /> Add Complexity Level
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{editing ? 'Edit Complexity Level' : 'New Complexity Level'}</h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Advanced"
              maxLength={100}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description (used by the AI)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="e.g. Complex, open-ended problems: edge cases, optimisation and design trade-offs."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={closeForm} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving && <LoadingSpinner size="sm" />}
              {editing ? 'Save' : 'Create'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {(levels || []).length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-500 text-center">No complexity levels yet.</p>
        ) : (
          (levels || []).map((level) => (
            <div key={level.id} className="flex items-start justify-between gap-4 px-5 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{level.name}</p>
                {level.description && <p className="text-xs text-gray-500 mt-0.5">{level.description}</p>}
              </div>
              {(canEdit || canDelete) && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {canEdit && (
                    <button
                      onClick={() => startEdit(level)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => confirmDelete(level)}
                      disabled={deleteMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded disabled:opacity-50"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default ComplexityLevelManagementPage
